/**
 * Хелперы для локализации SEO: работа с сообщениями i18n и локализованными URL/контентом.
 * Без зависимостей от I18nService — список локалей передаётся снаружи (см. localeValidation.ts).
 */

import { getSeoLangCode } from './seoLangCodes'

export type SupportedLocale = string
export type SeoMessages = Record<string, any>

export interface LocalizedContent {
  title?: Record<string, string>
  description?: Record<string, string>
  slug?: Record<string, string>
}

const DEFAULTS = {
  siteName: 'Bible School',
  title: 'Bible School - Free Biblical Education Online',
  description: 'Join our free online Bible school. Learn practical biblical principles for everyday life. Expert instructors, certificates, and spiritual growth.',
} as const

/** Читает вложенный ключ из объекта (path как 'layout.footer.main.logoAlt'). KISS. */
function getByPath(obj: Record<string, any> | null | undefined, path: string): string | undefined {
  if (!obj) return undefined
  const keys = path.split('.')
  let cur: any = obj
  for (const k of keys) {
    cur = cur?.[k]
    if (cur === undefined) return undefined
  }
  return typeof cur === 'string' ? cur : undefined
}

/** Берёт строку по пути из primary, иначе из secondary, иначе default. */
function getFromMessages(
  primary: SeoMessages | null | undefined,
  secondary: SeoMessages | null | undefined,
  path: string,
  def: string
): string {
  return getByPath(primary, path) ?? getByPath(secondary, path) ?? def
}

/** SEO-конфиг из i18n: index.seo.title/description, layout.footer.main.logoAlt. */
export function getLocalizedSeoConfig(
  locale: string,
  messages?: SeoMessages | null,
  fallback?: SeoMessages | null
) {
  const primary = messages ?? fallback
  const secondary = fallback ?? messages
  const get = (path: string, def: string) => getFromMessages(primary, secondary, path, def)

  return {
    siteName: get('layout.footer.main.logoAlt', DEFAULTS.siteName),
    defaultTitle: get('index.seo.title', DEFAULTS.title),
    defaultDescription: get('index.seo.description', DEFAULTS.description),
    locale,
    langCode: getSeoLangCode(locale),
  }
}

/** Значение для локали: data[locale] ?? data[fallbackLocale] ?? первое значение. */
export function getLocalizedValue<T>(
  data: Record<string, T> | T,
  locale: string,
  fallbackLocale: string = 'en'
): T {
  if (typeof data !== 'object' || data === null) return data as T
  const map = data as Record<string, T>
  return (map[locale] ?? map[fallbackLocale] ?? Object.values(map)[0]) as T
}

/** URL страницы для локали: baseUrl/locale/path (единый шаблон для всех). */
export function createLocalizedUrl(
  baseUrl: string,
  slug: string | Record<string, string>,
  locale: string
): string {
  const path = (typeof slug === 'string' ? slug : getLocalizedValue(slug, locale)).replace(/^\//, '')
  const base = baseUrl.replace(/\/$/, '')
  return path ? `${base}/${locale}/${path}` : `${base}/${locale}`
}

/** Собирает абсолютный URL: если path уже с http — вернуть как есть, иначе baseUrl + path. */
function toAbsoluteUrl(baseUrl: string, path: string): string {
  if (path.startsWith('http')) return path
  return path.startsWith('/') ? `${baseUrl}${path}` : `${baseUrl}/${path}`
}

/**
 * Альтернативные hreflang-ссылки. Список локалей передаётся снаружи.
 */
export function createAlternateLinks(
  baseUrl: string,
  currentPath: string,
  supportedLocales: readonly string[],
  slugs?: Record<string, string>
): Array<{ rel: string; hreflang: string; href: string }> {
  const pathWithoutLocale = currentPath.replace(new RegExp(`^/(${supportedLocales.join('|')})`), '') || '/'
  const pathWithLocale = (loc: string) => pathWithoutLocale === '/' ? `/${loc}` : `/${loc}${pathWithoutLocale}`

  const links = supportedLocales.map((locale) => {
    const pathOrSlug = slugs?.[locale] ?? pathWithLocale(locale)
    return { rel: 'alternate' as const, hreflang: getSeoLangCode(locale), href: toAbsoluteUrl(baseUrl, pathOrSlug) }
  })

  const defaultHref = toAbsoluteUrl(baseUrl, slugs?.uk ?? pathWithLocale('uk'))
  links.push({ rel: 'alternate', hreflang: 'x-default', href: defaultHref })

  return links
}

/** Schema.org с workTranslation для остальных локалей. */
export function createMultilingualSchema(
  baseSchema: Record<string, any>,
  localizedContent: LocalizedContent,
  currentLocale: string,
  baseUrl: string,
  supportedLocales: readonly string[]
): Record<string, any> {
  const slugMap = localizedContent.slug
  const others = slugMap ? supportedLocales.filter((loc) => loc !== currentLocale && slugMap[loc]) : []
  const workTranslation =
    others.length > 0
      ? others.map((loc) => ({
          '@type': 'WebPage',
          url: createLocalizedUrl(baseUrl, slugMap!, loc),
          inLanguage: getSeoLangCode(loc),
        }))
      : undefined
  return {
    ...baseSchema,
    inLanguage: getSeoLangCode(currentLocale),
    ...(workTranslation && { workTranslation }),
  }
}
